import React, { useContext, Suspense } from 'react';
import { PapiDeviation, RelatedBlock } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import LazyContext from '../LazyLoadingImage/LazyContext';
import SidebarBlock from './SidebarBlock';
import LoaderBlock from './_partials/LoaderBlock';
import RWait from '@wix/da-react-app/pkg/client/ResourceCache/RWait';
import { useSelector } from 'react-redux';
import { getIsDuperbrowseActive } from '../Duperbrowse/redux/selectors';

/**
 * Returns `true` if the supplied {@link block} is one we've provided an alternative placement for on mobile
 */
export function shouldRelocateBlockOnMobile(block: RelatedBlock) {
  // Monetization promos that aren't the dreamup one have a higher placement on mobile
  if (
    block.contentType.startsWith('monetization_promotion') &&
    block.contentType !== 'monetization_promotion_dreamup_thirdparty' &&
    block.contentType !== 'monetization_promotion_dreamup_firstparty'
  ) {
    return true;
  }

  // adoptable_info, pcp_info and originals blocks have a higher placement on mobile
  if (
    ['adoptable_info', 'pcp_info', 'originals_info', 'other_originals'].includes(block.contentType)
  ) {
    return true;
  }

  return false;
}

function shouldRenderSidebarBlock(block: RelatedBlock, isDesktop: boolean) {
  return isDesktop ? true : !shouldRelocateBlockOnMobile(block);
}

function renderSidebarBlocks(
  blocks: RelatedBlock[] | undefined,
  isDesktop: boolean,
  deviation: PapiDeviation
) {
  return (
    <>
      {blocks &&
        blocks.map(
          (block, index) =>
            shouldRenderSidebarBlock(block, isDesktop) && (
              <LazyContext.Provider value={isDesktop ? index > 1 : true} key={block.contentType}>
                <BiLoggerContextProvider value={{ _rankOffset: 0 }}>
                  <SidebarBlock block={block} deviation={deviation} />
                </BiLoggerContextProvider>
              </LazyContext.Provider>
            )
        )}
    </>
  );
}

export interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const LoadingSections: React.FC<{
  relatedIsReady: boolean;
  blocks?: RelatedBlock[];
  isDesktop: boolean;
  deviation: PapiDeviation;
}> = ({ relatedIsReady, blocks, isDesktop, deviation }) => {
  const isSSR = typeof document !== 'undefined';
  if (!isSSR && relatedIsReady) {
    return renderSidebarBlocks(blocks, isDesktop, deviation);
  }
  return (
    <>
      <LoaderBlock index={0} />
      <LoaderBlock index={1} />
      <LoaderBlock index={2} />
    </>
  );
};

const Sidebar: React.FC<React.PropsWithChildren<Props>> = ({ deviation, className }) => {
  const isDuperbrowse = useSelector(getIsDuperbrowseActive);
  const isDesktop = !useContext(MobileContext);
  const blocks = deviation?.extended?.relatedContent;
  const isLoading = !deviation.extended;
  // Related is ready when we get the blocks OR in duperbrowse where we dont lazy
  // fetch the related blocks meaning it comes with extended
  const relatedIsReady = !isLoading && (blocks || isDuperbrowse);

  return (
    <ErrorBoundary debugComponent="Sidebar">
      <div className={className} role="complementary">
        <Suspense
          name="sidebar-suspense"
          fallback={
            <LoadingSections
              relatedIsReady={!!relatedIsReady}
              blocks={blocks}
              isDesktop={isDesktop}
              deviation={deviation}
            />
          }
        >
          <RWait key="relatedContent" cacheName="relatedContent" skipCache={!!relatedIsReady} />
          {relatedIsReady && renderSidebarBlocks(blocks, isDesktop, deviation)}
          {!relatedIsReady && (
            <LoadingSections
              relatedIsReady={!!relatedIsReady}
              blocks={blocks}
              isDesktop={isDesktop}
              deviation={deviation}
            />
          )}
        </Suspense>
      </div>
    </ErrorBoundary>
  );
};
Sidebar.displayName = 'Sidebar';

export default Sidebar;
